import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { AccountModelPrivatization } from "../../../model/privatization/AccountModelPrivatization";
import { BranchLookupService } from "./BranchLookupService";
import { BranchPromiseLookupService } from "./BranchPromiseLookupService";
import { AccountLookupService } from "./AccountLookupService";
import { CubiqWorker } from "../../../worker/CubiqWorker";
import { IAccountPromiseLookupService } from "../IAccountPromiseLookupService";
import { PrimaryLookupService } from "../../impl/PrimaryLookupService";

import { ClientPromiseLookupService } from "./ClientPromiseLookupService";
import { ClientAccountVisibilityType } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityType";
import { ClientAccountVisibilityService } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityService";
import { LoggerService } from "@bfx/logger";
import { AutoCompleteLabelValuePrimaryLookupService } from "./AutoCompleteLabelValuePrimaryLookupService";

const logger = LoggerService.for(__dirname);

@Injectable()
export class AccountPrimaryLookupService
  extends AutoCompleteLabelValuePrimaryLookupService<string, CubiqTS.AccountImpl>
  implements IAccountPromiseLookupService
{
  constructor(
    protected base: AccountLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
    protected branchLookup: BranchPromiseLookupService,
    protected accountModelPrivatization: AccountModelPrivatization,
    clientPromiseLookup: ClientPromiseLookupService,
    protected clientAccountVisibilityService: ClientAccountVisibilityService,
  ) {
    super(base, factory, CubiqTS.AccountImpl, cubiqWorker, "AccountImpl");
    let accountsObservable = cubiqWorker
      .primeStaticData<CubiqTS.AccountImpl>(CubiqTS.AccountImpl.name)
      .subscribe(
        (accounts: CubiqTS.AccountImpl[]) => {
          Promise.all([
            cubiqWorker.isAuth(`${this.constructor.name}`),
            this.accountModelPrivatization.ready,
            clientPromiseLookup.isPrimed(),
            clientAccountVisibilityService.isReady(),
          ]).then(
            () => {
              this.base.prime(accounts);
            },
            (reason) => {
              console.debug("AccountPrimaryLookupService: Failed to prime: " + reason);

              this._primed.resolve(false);
            },
          );
        },
        (error: Error) => {
          console.debug("Account prime failed");
          this._primed.reject(error);
        },
        () => {
          this.addPrivatization(cubiqWorker);
          this._primed.resolve(true);
        },
      );

    this.broker.registerMethod("getAccount", [PRIMITIVE], this.getAccount.bind(this), this.VALUE);
    this.broker.registerMethod(
      "getAccountsForClient",
      [PRIMITIVE],
      this.getAccountsForClient.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupAccount",
      [PRIMITIVE, PRIMITIVE],
      this.lookupAccount.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupAccountsForId",
      [PRIMITIVE, PRIMITIVE],
      this.lookupAccountsForId.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupAccountsForName",
      [PRIMITIVE, PRIMITIVE],
      this.lookupAccountsForName.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupAccountsForBookingCode",
      [PRIMITIVE, PRIMITIVE],
      this.lookupAccountsForBookingCode.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupAccountsForContact",
      [PRIMITIVE, PRIMITIVE],
      this.lookupAccountsForBookingCode.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupAccountsForClient",
      [PRIMITIVE, PRIMITIVE, PRIMITIVE],
      this.lookupAccountsForClient.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getBreakAccounts",
      [PRIMITIVE],
      this.getBreakAccounts.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getBreakAndClientAccounts",
      [PRIMITIVE, PRIMITIVE],
      this.getBreakAndClientAccounts.bind(this),
      this.VALUE,
    );
    //
    this.broker.registerMethod(
      "getActiveAccount",
      [PRIMITIVE],
      this.getActiveAccount.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getActiveAccountsForClient",
      [PRIMITIVE],
      this.getActiveAccountsForClient.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getActiveBreakAccounts",
      [PRIMITIVE],
      this.getActiveBreakAccounts.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getActiveBreakAndClientAccounts",
      [PRIMITIVE, PRIMITIVE],
      this.getActiveBreakAndClientAccounts.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupActiveAccountsForId",
      [PRIMITIVE, PRIMITIVE],
      this.lookupActiveAccountsForId.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupActiveAccountsForName",
      [PRIMITIVE, PRIMITIVE],
      this.lookupActiveAccountsForName.bind(this),
      this.VALUE,
    );
    /*   this.broker.registerMethod('lookupActiveAccountsForBookingCode', [PRIMITIVE, PRIMITIVE], this.lookupActiveAccountsForBookingCode.bind(this), this.VALUE);
      this.broker.registerMethod('lookupActiveAccountsForContact', [PRIMITIVE, PRIMITIVE], this.lookupActiveAccountsForBookingCode.bind(this), this.VALUE);
      this.broker.registerMethod('lookupActiveAccountsForClient', [PRIMITIVE, PRIMITIVE, PRIMITIVE], this.lookupActiveAccountsForClient.bind(this), this.VALUE);
 */
    this.broker.registerMethod(
      "lookupBMarkAccountsForId",
      [PRIMITIVE, PRIMITIVE],
      this.lookupBMarkAccountsForId.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupBMarkAccountsForName",
      [PRIMITIVE, PRIMITIVE],
      this.lookupBMarkAccountsForName.bind(this),
      this.VALUE,
    );
  }

  private addPrivatization(cubiqWorker: CubiqWorker): void {
    for (let kv of this.accountModelPrivatization.getPrivate().entries()) {
      this.base.mapPrivatizationItem(kv[1], kv[0]);
    }

    this.branchLookup.isPrimed().then((primed) => {
      let baseBranchLookup = this.branchLookup.getBase() as BranchLookupService;

      if (!primed) {
        console.debug(`ClientPrimaryLookupService: failed to access branches for privatization`);
        return;
      }

      for (let branchCd of baseBranchLookup.getVisiblePrivateBranchCodes()) {
        for (let kv of this.accountModelPrivatization.getBranchPrivate(branchCd).entries()) {
          this.base.mapPrivatizationItem(kv[1], kv[0]);
        }
      }
    });

    for (let kv of this.accountModelPrivatization.getRestricted().entries()) {
      this.base.mapPrivatizationItem(kv[1], kv[0]);
    }

    this.accountModelPrivatization.restricted$.subscribe((restrictedClientSegment) => {
      this.base.mapPrivatizationItem(restrictedClientSegment.value, restrictedClientSegment.key);
    });
  }

  getAccount(id: string, type: ClientAccountVisibilityType | null): Promise<CubiqTS.AccountImpl> {
    return new Promise<CubiqTS.AccountImpl>((res, rej) => {
      res(this.base.getAccount(id, type));
    });
  }

  getClientsForAccount(id: string): Promise<CubiqTS.ClientImpl[]> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      res(this.base.getClientsForAccount(id));
    });
  }

  getAccountsForClient(id: string): Promise<CubiqTS.AccountImpl[]> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(this.base.getAccountsForClient(id));
    });
  }

  lookupAccount(namePrefix: string, maxResults: number): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(this.base.lookupAccount(namePrefix, maxResults));
    });
  }

  /* same as lookupAccount() but more efficient */
  lookupAccountsForId(
    searchToken: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(this.base.lookupAccountsForId(searchToken, maxResults));
    });
  }

  lookupAccountsForName(
    searchToken: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(this.base.lookupAccountsForName(searchToken, maxResults));
    });
  }

  lookupAccountsForBookingCode(
    searchToken: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(this.base.lookupAccountsForBookingCode(searchToken, maxResults));
    });
  }

  lookupAccountsForContact(
    searchToken: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(this.base.lookupAccountsForContact(searchToken, maxResults));
    });
  }

  lookupAccountsForClient(
    searchToken: string,
    clientCd: string,
    maxResults: number,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(this.base.lookupAccountsForClient(searchToken, clientCd, maxResults));
    });
  }

  getBreakAccounts(salesGroup: string): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(this.base.getBreakAccounts(salesGroup));
    });
  }

  getBreakAndClientAccounts(
    clientCd: string,
    salesGroup: string,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      let accounts: Array<CubiqTS.AccountImpl> = [];
      let clientAccounts = this.base.getAccountsForClient(clientCd);
      let breakAccounts = this.base.getBreakAccounts(salesGroup);

      if (
        clientAccounts != null &&
        clientAccounts.length > 0 &&
        breakAccounts != null &&
        breakAccounts.length > 0
      ) {
        accounts = clientAccounts.concat(
          breakAccounts.filter(function (item) {
            return clientAccounts.indexOf(item) < 0;
          }),
        );
      } else if (clientAccounts != null && clientAccounts.length > 0) {
        accounts = [].concat(clientAccounts);
      } else if (breakAccounts != null && breakAccounts.length > 0) {
        accounts = [].concat(breakAccounts);
      }
      res(accounts);
    });
  }
  /* ******************************************************** */
  /*  ACTIVE ACCOUNTS */
  getActiveAccount(
    id: string,
    type: ClientAccountVisibilityType | null,
  ): Promise<CubiqTS.AccountImpl> {
    return new Promise<CubiqTS.AccountImpl>((res) => {
      res(this.base.getActiveAccount(id, type));
    });
  }

  getActiveAccountsForClient(id: string): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res) => {
      res(this.base.getAccountsForClient(id, true));
    });
  }

  getActiveBreakAccounts(salesGroup: string): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res) => {
      res(this.base.getBreakAccounts(salesGroup, true));
    });
  }

  getActiveBreakAndClientAccounts(
    clientCd: string,
    salesGroup: string,
    onlyFullVisibility?: boolean,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      let accounts: Array<CubiqTS.AccountImpl> = [];
      let clientAccounts = this.base.getAccountsForClient(clientCd, true);
      let breakAccounts = this.base.getBreakAccounts(salesGroup, true);
      if (
        clientAccounts != null &&
        clientAccounts.length > 0 &&
        breakAccounts != null &&
        breakAccounts.length > 0
      ) {
        accounts = clientAccounts.concat(
          breakAccounts.filter(function (item) {
            return clientAccounts.indexOf(item) < 0;
          }),
        );
      } else if (clientAccounts != null && clientAccounts.length > 0) {
        accounts = [].concat(clientAccounts);
      } else if (breakAccounts != null && breakAccounts.length > 0) {
        accounts = [].concat(breakAccounts);
      }
      res(onlyFullVisibility ? this.filterFullyVisibleClients(accounts, 0) : accounts);
    });
  }

  lookupActiveAccountsForId(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupAccountsForId(searchToken, 0, true),
              maxResults,
            )
          : this.base.lookupAccountsForId(searchToken, maxResults, true),
      );
    });
  }

  lookupActiveAccountsForName(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupAccountsForName(searchToken, 0, true),
              maxResults,
            )
          : this.base.lookupAccountsForName(searchToken, maxResults, true),
      );
    });
  }

  lookupBMarkAccountsForId(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Promise<CubiqTS.AccountImpl[]> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupAccountsForId(searchToken, 0, true, true),
              maxResults,
            )
          : this.base.lookupAccountsForId(searchToken, maxResults, true, true),
      );
    });
  }
  lookupBMarkAccountsForName(
    searchToken: string,
    maxResults: number,
    onlyFullVisibility?: boolean,
  ): Promise<CubiqTS.AccountImpl[]> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupAccountsForName(searchToken, 0, true, true),
              maxResults,
            )
          : this.base.lookupAccountsForName(searchToken, maxResults, true, true),
      );
    });
  }

  protected processModelUpdateEvent(event: CubiqTS.ModelUpdateImpl): void {
    super.processModelUpdateEvent(event);
  }
  // not in use
  /* lookupActiveAccountsForBookingCode(searchToken:string, maxResults:number):Promise<Array<CubiqTS.AccountImpl>> {
        return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
            res(this.base.lookupAccountsForBookingCode(searchToken, maxResults, true));
        });
    }

    lookupActiveAccountsForContact(searchToken:string, maxResults:number):Promise<Array<CubiqTS.AccountImpl>> {
        return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
            res(this.base.lookupAccountsForContact(searchToken, maxResults, true));
        });
    }

    lookupActiveAccountsForClient(searchToken:string, clientCd:string, maxResults:number):Promise<Array<CubiqTS.AccountImpl>> {
        return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
            res(this.base.lookupAccountsForClient(searchToken, clientCd, maxResults, true));
        });
    } */

  /// OMS-31021
  // Filters the dataset of accounts, based on users visibility, only fully visible accounts are returend
  private filterFullyVisibleClients(
    dataset: Array<CubiqTS.AccountImpl>,
    maxResults: number,
  ): Array<CubiqTS.AccountImpl> {
    if (Array.isArray(dataset) && dataset.length > 0) {
      let result: Array<CubiqTS.AccountImpl> = new Array<CubiqTS.AccountImpl>();
      for (let account of dataset) {
        if (
          account instanceof CubiqTS.AccountImpl &&
          this.clientAccountVisibilityService._getVisibilityType(account) ===
            ClientAccountVisibilityType.Full
        ) {
          result.push(account);
        }

        if (maxResults > 0 && result.length >= maxResults) {
          break;
        }
      }
      return result;
    } else {
      return [];
    }
  }
}
